import { useSyncExternalStore } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL SERVICE WORKER, REGISTRADO. Y el aviso de versión nueva que su modo pide.
 *
 * El `sw.js` se construía en cada build y NO LO REGISTRABA NADIE:
 * `injectRegister: null` en `vite.config.ts` quita el registro automático, el
 * `tsconfig` ya declaraba los tipos de `virtual:pwa-register`… y la llamada no
 * existía. Consecuencias, las dos graves:
 *
 *  · LA APP NO FUNCIONABA SIN CONEXIÓN. Ni precache ni navegación offline, por
 *    mucho que el PRD, el presupuesto y los comentarios dijeran «offline-first».
 *    Todo lo que se había razonado sobre qué entra en el precache —el fondo en
 *    `webp`, el chunk del rewind— se construía y no se instalaba nunca.
 *  · LOS RECORDATORIOS COLGABAN: `serviceWorker.ready` no se resuelve nunca sin
 *    service worker. Ya tienen su tope (ver `recordatorios.ts`), pero la vía
 *    buena es esta.
 *
 * `registerType: 'prompt'` significa que la versión nueva NO se aplica sola:
 * espera a que el usuario la acepte, porque recargar a mitad de un pomodoro es
 * hostil. Eso exige un aviso, y tampoco había: el `SKIP_WAITING` de `sw.ts` era
 * código al que no llegaba nadie. El aviso está en `features/aviso-version.tsx`.
 *
 * En desarrollo `virtual:pwa-register` es un sustituto que no registra nada
 * (`devOptions.enabled: false`): un service worker en desarrollo cachea el
 * código que estás cambiando, y es la forma más rápida de perder una tarde.
 * ═══════════════════════════════════════════════════════════════════════════
 */

let aplicar: ((recargar?: boolean) => Promise<void>) | null = null;
let hayNueva = false;
const oyentes = new Set<() => void>();

export async function registrarServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator) || aplicar) return;
  const { registerSW } = await import('virtual:pwa-register');
  aplicar = registerSW({
    immediate: true,
    onNeedRefresh() {
      hayNueva = true;
      for (const oyente of oyentes) oyente();
    },
    onRegisterError(error: unknown) {
      // Un navegador que no deja registrar —modo privado de Firefox, un
      // origen sin HTTPS— no rompe la app: sigue funcionando con red.
      console.error('[sw] no se pudo registrar', error);
    },
  });
}

/**
 * Instala la versión nueva y recarga. Solo cuando el usuario lo pide.
 *
 * La recarga la hace `registerSW` cuando el service worker nuevo toma el control.
 * Si ese aviso no llega —el iPhone instalado no siempre lo da—, se recarga igual a
 * los 4 s: para entonces el nuevo ya está activo y la recarga entra con él. Sin
 * esto la pantalla de carga se quedaría esperando para siempre.
 */
export function aplicarVersionNueva(): void {
  void aplicar?.(true);
  window.setTimeout(() => window.location.reload(), 4000);
}

function suscribir(oyente: () => void): () => void {
  oyentes.add(oyente);
  return () => oyentes.delete(oyente);
}

export function useHayVersionNueva(): boolean {
  return useSyncExternalStore(suscribir, () => hayNueva);
}
